/// <amd-module name="Environments/ProviderCreateDetails/VirtualMachines/Components/VirtualMachineGenericProviderComponent" />
import { PackageMetadata } from "DistributedTask/Client/Contracts/TaskAgent";

import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { StoreManager } from "Environments/Flux/StoreManager";
import { VirtualMachineResourceActionCreator } from "Environments/ProviderCreateDetails/VirtualMachines/ActionsCreators/VirtualMachineResourceActionCreator";
import * as Resources from "Environments/ProviderCreateDetails/VirtualMachines/Resources";
import { VirtualMachineResourceStore } from "Environments/ProviderCreateDetails/VirtualMachines/Stores/VirtualMachineResourceStore";
import { copyToClipboard, Operations, OperationSystem, PageKey, ResourceTargetConstants, VirtualMachineConfigurationScripts } from "Environments/ProviderCreateDetails/VirtualMachines/Utilities";

import * as React from "react";

import * as StringUtils from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

export interface IVirtualMachineGenericProviderProps {
    environmentName: string;
    instanceId: string;
    environmentId: number;
    copyScriptEnabled?: boolean;
}

export interface IVirtualMachineGenericProviderState extends IEnvironmentBaseComponentState {
    selectedOperatingSystem: string;
    packages: {
        [key: string]: PackageMetadata
    };
}

export class VirtualMachineGenericProviderComponent extends EnvironmentBaseComponent<IVirtualMachineGenericProviderProps, IVirtualMachineGenericProviderState> {
    public getInstanceId(context: IVssComponentContext): string {
        return "add-virtualmachines-generic-instance-id";
    }

    constructor(props: IVirtualMachineGenericProviderProps, context: IVssComponentContext) {
        super(props, context);
        
        this.state = this._initializeState();

        this._vmActionCreator = ActionCreatorManager.CreateActionCreator(VirtualMachineResourceActionCreator, this.props.instanceId);
        this._vmStore = StoreManager.CreateStore<VirtualMachineResourceStore>(VirtualMachineResourceStore, this.props.instanceId);

        this._vmStore.addListener(Operations.UpdatePackage, this._onGetTargetPackagesOperation);
        
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions> (MessagesActions, this.props.instanceId);

        this._vmActionCreator.getTargetPackages(this.context.pageContext);

        this._osSelection.select(0);
    }

    public render(): React.ReactNode {
        return <>
            <FormItem
                label={Resources.OperatingSystem}
                className="default-margin-top"
            >
            {
                <Dropdown
                    items={this._getPlatforms()}
                    onSelect={this._onProviderTypeChange}
                    selection={this._osSelection}
                    className="vm-dropdown"
                    calloutContentClassName={"vm-dropdown-callout"}
                />
            }
            </FormItem>
            {
                Object.keys(this.state.packages).length > 0 &&
                    <div className={"default-margin-top"}>
                        <div className="vm-registration-script-header">
                            <label>{Resources.RegistrationScript}</label>
                        </div>
                        <div className={"vm-sugg-msg secondary-text"}>
                            {this._getSuggestionCommandMessage(
                                this.state.selectedOperatingSystem == OperationSystem.Windows? Resources.RegistrationScriptFormatWindows : Resources.RegistrationScriptFormatLinux
                                , this._getScript())}
                            <div className={"warning-message-container"}>
                                <MessageBar severity={MessageBarSeverity.Warning} messageClassName = {"pat-token-font"}> {Resources.PatTokenCopied} </MessageBar>
                            </div>
                            {this._getSuggestionCommandMessage(Resources.RegistrationScriptFormatLine2, "")}
                        </div>
                    </div>
            }
        </>;
    }

    private _onProviderTypeChange = (event: any, selectedProvider: IListBoxItem<{}>) => {
        this.setState({
            selectedOperatingSystem: selectedProvider.id
        });
    }

    private _onGetTargetPackagesOperation = (): void => {
        this.setState({
            packages: this._vmStore.getState().targetPackages
        });
    }

    private _initializeState(): IVirtualMachineGenericProviderState {
        return {
            packages: {},
            selectedOperatingSystem: OperationSystem.Windows,
            errorMessages: {}
        };
    }
    
    private _getPlatforms(): IListBoxItem<{}>[] {
        const providerTypes =[
            { id: OperationSystem.Windows, text: Resources.Windows },
            { id: OperationSystem.Linux, text: Resources.Linux }
        ];
        
        return providerTypes
    }

    private _getCollectionParams(): string {
        let collectionName = "";
        const isOnPremise: boolean = !PageContextHelper.isHostedType(this.context.pageContext);

        if (isOnPremise) {
            collectionName = StringUtils.format(VirtualMachineConfigurationScripts.CollectionName, PageContextHelper.getCollectionName(this.context.pageContext));
        }

        return collectionName;
    }

    private _getProjectParams(): string {
        let projectParams = "";

        const projectName = PageContextHelper.getProjectName(this.context.pageContext);
        projectParams = StringUtils.format(VirtualMachineConfigurationScripts.ProjectParams, projectName);

        return projectParams;
    }

    private _getScript(): string {
        const agentDownloadUrl = this._getAgentDownloadUrl(this.state.selectedOperatingSystem);
        if (!agentDownloadUrl) {
            return "";
        }

        const url = PageContextHelper.getAccountUri(this.context.pageContext);
        const collectionParams: string = this._getCollectionParams();
        const projectParams = this._getProjectParams();
        const resourceParams = this._getResourceParams();
        const authParameters: string = this._getAuthParams();
        
        if (StringUtils.equals(this.state.selectedOperatingSystem, OperationSystem.Linux)) {
            this._environmentscript = StringUtils.format(VirtualMachineConfigurationScripts.Linux, agentDownloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
        }
        else {
            const winConfigScript: string = StringUtils.format(VirtualMachineConfigurationScripts.Windows, agentDownloadUrl, resourceParams, url, collectionParams, projectParams, authParameters);
            const agentConfigurationPSAdminPromptCheck: string = StringUtils.format(VirtualMachineConfigurationScripts.PSAdminPromptCheck, Resources.RunCommandInAdministratorPowerShellPrompt);
            const agentConfigurationPSMinVersionCheck: string = StringUtils.format(VirtualMachineConfigurationScripts.PSMinVersionCheck, Resources.PSMinVersionCheck);
            this._environmentscript = agentConfigurationPSAdminPromptCheck.concat(agentConfigurationPSMinVersionCheck).concat(winConfigScript);
        }
        return this._environmentscript;
    }

    public componentWillUnmount(): void {
        this._vmStore.removeListener(Operations.UpdatePackage, this._onGetTargetPackagesOperation);
        StoreManager.DeleteStore<VirtualMachineResourceStore>(VirtualMachineResourceStore, this.props.instanceId);
        ActionCreatorManager.DeleteActionCreator<VirtualMachineResourceActionCreator>(VirtualMachineResourceActionCreator, this.props.instanceId);
    }

    private _getAuthParams(): string {
        let authParameters: string = "";
        const isSchemeHttp: boolean = !PageContextHelper.isSSLEnabled(this.context.pageContext);

        if(isSchemeHttp) {
            if (StringUtils.equals(this.state.selectedOperatingSystem, OperationSystem.Windows)) {
                authParameters = VirtualMachineConfigurationScripts.AuthParamIntegrated;
            }
            else {
                authParameters = VirtualMachineConfigurationScripts.AuthParamNegotiate;
            }
        }
        else {
            authParameters = StringUtils.format(VirtualMachineConfigurationScripts.AuthParameters, this._insertToken);
        }

        return authParameters;
    }

    private _getResourceParams(): string {
        return StringUtils.format(VirtualMachineConfigurationScripts.EnvironmentNameParams, this.props.environmentName);
    }

    private _getAgentDownloadUrl(platform: string): string | undefined {
        if (!this.state.packages) {
            return undefined;
        }
        else if (StringUtils.equals(platform, OperationSystem.Windows)) {
            const win_package = this.state.packages[ResourceTargetConstants.Windows];
            if (win_package) {
                return win_package.downloadUrl;
            }
        }
        else if (StringUtils.equals(platform, OperationSystem.Linux)) {
            const linux_package = this.state.packages[ResourceTargetConstants.Linux];
            if (linux_package) {
                return linux_package.downloadUrl;
            }
        }
        
        return undefined;
    }

    private _getSuggestionCommandMessage(format: string, command: string): React.ReactNode {
        return (
            <FormatComponent format={format} className={"body-s vm-sugg-msg-text flex-row nowrap"} elementType={"div"} >
                <div className={"shell-command fontSizeS flex-row"}>
                    <div className={"overflow-ellipsis command-text"}>{command}</div>
                    <Button tooltipProps={{ text: Resources.CopyCommand }} onClick={this._onClickHandler} ariaLabel={Resources.CopyCommand}>
                        <Icon iconName={"Copy"} />
                    </Button>
                </div>
            </FormatComponent>
        );
    }

    private _onClickHandler = () => {
        let command = this._environmentscript;
        this._OnClick(command);
    }

    private _OnClick(command: string) {
        this._messageActions.clearMessage.invoke(PageKey.AddEnvironmentPAT);
        if(command.indexOf(this._insertToken) !== -1)
        {
            if(this._environmentPATToken === '')
            {
                const environmentService = this.context.pageContext.getService<IEnvironmentService>("IEnvironmentService");
                environmentService.getEnvironmentScopedPAT(this.props.environmentId).then( (environmentPATtoken : string) => {
                    this._environmentPATToken = environmentPATtoken;
                    command = command.split(this._insertToken).join(this._environmentPATToken);
                    copyToClipboard(command);
                },
                (error) => {
                    this._messageActions.addMessage.invoke({ key: PageKey.AddEnvironmentPAT, value: error.message || error });
                } );
            }
            else
            {
                command = command.split(this._insertToken).join(this._environmentPATToken);
                copyToClipboard(command);
            }
        }
        else
        {
            copyToClipboard(command);
        }
    }

    private _vmActionCreator: VirtualMachineResourceActionCreator;
    private _vmStore: VirtualMachineResourceStore;
    private _osSelection = new DropdownSelection();
    private _messageActions: MessagesActions;
    private _environmentPATToken: string = '';
    private _environmentscript: string = '';
    private readonly _insertToken : string =  "__token__";
}